import { NEGATIVE_OPTION_ALIAS } from 'kosmosConfig';
import {
  OptionDto,
  QuestionConditionBoolDto,
  QuestionConditionDto,
  QuestionConditionMultipleChoiceDto,
  QuestionConditionNumberDto,
  QuestionConditionNumberOperator,
} from 'models/dtoTypes';

type QuestionCondition =
  | QuestionConditionDto
  | QuestionConditionBoolDto
  | QuestionConditionMultipleChoiceDto
  | QuestionConditionNumberDto;

type FileValue = {
  data: string;
  file: File;
};
type ValueType =
  | string
  | number
  | boolean
  | FileValue
  | FileValue[]
  | string[]
  | moment.Moment
  | OptionDto
  | OptionDto[];

interface Attributes {
  [key: string]: ValueType;
}

const operators = {
  Equal: 'Equal',
  NotEqual: 'NotEqual',
  GreaterThan: 'GreaterThan',
  LessThan: 'LessThan',
};

function matchNumberCondition(
  expectedValue: number,
  currentValue: number,
  operator?: QuestionConditionNumberOperator,
): boolean {
  switch (operator) {
    case operators.Equal:
      return currentValue === expectedValue;
    case operators.NotEqual:
      return currentValue !== expectedValue;
    case operators.GreaterThan:
      return currentValue > expectedValue;
    case operators.LessThan:
      return currentValue < expectedValue;
    default:
      return true;
  }
}

function matchMultipleChoiceCondition(expectedValue: string, currentValue: OptionDto[]): boolean {
  if (expectedValue === '') {
    return !(currentValue.length === 1 && currentValue[0].alias === NEGATIVE_OPTION_ALIAS);
  }
  return currentValue
    .filter((o) => o.alias !== '')
    .map((o) => o.alias)
    .includes(expectedValue);
}

function matchSingleCondition(condition: QuestionCondition, attributes: Attributes): boolean {
  const { conditionQuestionAlias } = condition;
  // for select question, value will be a list of select optionDto
  const currentValue = conditionQuestionAlias ? attributes[conditionQuestionAlias] : null;

  switch (condition.type) {
    case 'QuestionConditionMultipleChoiceDto': {
      // if optionAlias exist, only show when selected matches,
      // else as long as target select any option
      const { optionAlias } = condition as QuestionConditionMultipleChoiceDto;

      if (optionAlias) {
        return Array.isArray(currentValue)
          ? matchMultipleChoiceCondition(optionAlias ?? '', currentValue as OptionDto[])
          : optionAlias === (currentValue as OptionDto)?.alias;
      } else {
        return Array.isArray(currentValue)
          ? matchMultipleChoiceCondition(optionAlias || '', currentValue as OptionDto[])
          : Boolean(currentValue);
      }
    }
    case 'QuestionConditionNumberDto': {
      const { value, operator } = condition as QuestionConditionNumberDto;
      return matchNumberCondition(value ?? 0, currentValue as number, operator);
    }
    default: {
      const { value } = condition as QuestionConditionBoolDto;
      return value === currentValue;
    }
  }
}

function matchConditions(conditions?: QuestionCondition[], attributes?: Attributes): boolean {
  if (!conditions || conditions.length < 1) {
    return true;
  }
  const bools = conditions.map((condition) => matchSingleCondition(condition, attributes || {}));
  return !bools.includes(false);
}

export default matchConditions;
